import React, { useState, useEffect } from 'react';
import { MapPin, Save, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import DashboardLayout from '@/components/dashboard/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '@/services/api';

const EnderecoEmpresa = () => {
  const { empresa, atualizarEmpresa } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  
  const [endereco, setEndereco] = useState({
    logradouro: empresa?.endereco_logradouro || '',
    numero: empresa?.endereco_numero || '',
    complemento: empresa?.endereco_complemento || '',
    bairro: empresa?.endereco_bairro || '',
    cidade: empresa?.endereco_cidade || '',
    estado: empresa?.endereco_estado || '',
    cep: empresa?.endereco_cep || ''
  });
  
  // Para entregadores o endereço é opcional (GPS substitui)
  const isEntregador = empresa?.tipo_empresa === 'entregador';
  const required = !isEntregador;
  
  const handleSalvar = async (e) => {
    e.preventDefault();
    
    const isEntregador = empresa?.tipo_empresa === 'entregador';
    
    // Para entregadores, todos os campos são opcionais (GPS substitui o endereço fiscal).
    // Para clientes, validação básica permanece obrigatória.
    if (!isEntregador) {
      if (!endereco.logradouro || !endereco.numero || !endereco.bairro || 
          !endereco.cidade || !endereco.estado || !endereco.cep) {
        toast.error('Preencha todos os campos obrigatórios');
        return;
      }
    }
    
    setLoading(true);
    
    try {
      const response = await api.put(`/empresas/${empresa.id}/endereco`, endereco);
      
      if (response.data.success) {
        toast.success('Endereço atualizado com sucesso!');
        
        // Atualiza dados da empresa no contexto
        const empresaAtualizada = await api.get(`/empresas/${empresa.id}`);
        atualizarEmpresa(empresaAtualizada.data);
        
        // Volta para o dashboard
        const dashboardUrl = empresa.tipo_empresa === 'cliente' 
          ? '/dashboard/cliente' 
          : '/dashboard/entregador';
        navigate(dashboardUrl);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar endereço');
    } finally {
      setLoading(false);
    }
  };
  
  const handleCepChange = async (cep) => {
    setEndereco({...endereco, cep});
    
    // Remove caracteres não numéricos
    const cepLimpo = cep.replace(/\D/g, '');
    
    // Se tem 8 dígitos, busca no ViaCEP
    if (cepLimpo.length === 8) {
      try {
        const response = await fetch(`https://viacep.com.br/ws/${cepLimpo}/json/`);
        const data = await response.json();
        
        if (!data.erro) {
          setEndereco(prev => ({
            ...prev,
            logradouro: data.logradouro || prev.logradouro,
            bairro: data.bairro || prev.bairro,
            cidade: data.localidade || prev.cidade,
            estado: data.uf || prev.estado
          }));
          toast.success('CEP encontrado!');
        }
      } catch (error) {
        console.error('Erro ao buscar CEP:', error);
      }
    }
  };
  
  const voltarParaDashboard = () => {
    const dashboardUrl = empresa.tipo_empresa === 'cliente' 
      ? '/dashboard/cliente' 
      : '/dashboard/entregador';
    navigate(dashboardUrl);
  };

  return (
    <DashboardLayout title="Endereço da Empresa">
      <div className="max-w-2xl mx-auto">
        <Button 
          variant="ghost" 
          onClick={voltarParaDashboard}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Voltar
        </Button>
        
        <div className="bg-white rounded-lg border border-slate-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <MapPin className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {empresa?.tipo_empresa === 'entregador' ? 'Endereço (opcional)' : 'Endereço de Operação'}
              </h2>
              <p className="text-sm text-slate-500">
                {empresa?.tipo_empresa === 'entregador'
                  ? 'Para entregadores, este endereço é apenas referência fiscal. A localização real é capturada por GPS em tempo real.'
                  : 'Este endereço será usado como origem das suas entregas'}
              </p>
            </div>
          </div>
          
          {empresa?.tipo_empresa === 'entregador' && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-6 text-sm text-blue-800">
              <strong>Não precisa preencher agora.</strong> Você pode pular esta etapa e cadastrar
              depois. O app captura sua localização pelo GPS sempre que você estiver disponível
              para entregas.
              <div className="mt-3">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="border-blue-400 text-blue-700 hover:bg-blue-100"
                  onClick={() => navigate('/dashboard/entregador/documentos')}
                  data-testid="btn-pular-endereco"
                >
                  Pular e ir para Documentos
                </Button>
              </div>
            </div>
          )}
          
          {empresa?.endereco_latitude && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-6">
              <p className="text-sm text-green-800">
                <strong>Endereço geocodificado:</strong> {empresa.endereco_completo}
              </p>
              <p className="text-xs text-green-600 mt-1">
                Lat: {empresa.endereco_latitude?.toFixed(6)}, Lng: {empresa.endereco_longitude?.toFixed(6)}
              </p>
            </div>
          )}
          
          <form onSubmit={handleSalvar} className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-1">
                <label className="text-sm font-medium mb-1 block">CEP *</label>
                <Input
                  value={endereco.cep}
                  onChange={(e) => handleCepChange(e.target.value)}
                  placeholder="00000-000"
                  maxLength={9}
                  required={required}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-4 gap-4">
              <div className="col-span-3">
                <label className="text-sm font-medium mb-1 block">Logradouro {required && '*'}</label>
                <Input
                  value={endereco.logradouro}
                  onChange={(e) => setEndereco({...endereco, logradouro: e.target.value})}
                  placeholder="Rua, Avenida, etc."
                  required={required}
                />
              </div>
              <div className="col-span-1">
                <label className="text-sm font-medium mb-1 block">Número {required && '*'}</label>
                <Input
                  value={endereco.numero}
                  onChange={(e) => setEndereco({...endereco, numero: e.target.value})}
                  placeholder="123"
                  required={required}
                />
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium mb-1 block">Complemento</label>
              <Input
                value={endereco.complemento}
                onChange={(e) => setEndereco({...endereco, complemento: e.target.value})}
                placeholder="Apto, Sala, Bloco, etc."
              />
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Bairro {required && '*'}</label>
                <Input
                  value={endereco.bairro}
                  onChange={(e) => setEndereco({...endereco, bairro: e.target.value})}
                  required={required}
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Cidade {required && '*'}</label>
                <Input
                  value={endereco.cidade}
                  onChange={(e) => setEndereco({...endereco, cidade: e.target.value})}
                  required={required}
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Estado {required && '*'}</label>
                <select
                  className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm"
                  value={endereco.estado}
                  onChange={(e) => setEndereco({...endereco, estado: e.target.value})}
                  required={required}
                >
                  <option value="">UF</option>
                  <option value="AC">AC</option>
                  <option value="AL">AL</option>
                  <option value="AP">AP</option>
                  <option value="AM">AM</option>
                  <option value="BA">BA</option>
                  <option value="CE">CE</option>
                  <option value="DF">DF</option>
                  <option value="ES">ES</option>
                  <option value="GO">GO</option>
                  <option value="MA">MA</option>
                  <option value="MT">MT</option>
                  <option value="MS">MS</option>
                  <option value="MG">MG</option>
                  <option value="PA">PA</option>
                  <option value="PB">PB</option>
                  <option value="PR">PR</option>
                  <option value="PE">PE</option>
                  <option value="PI">PI</option>
                  <option value="RJ">RJ</option>
                  <option value="RN">RN</option>
                  <option value="RS">RS</option>
                  <option value="RO">RO</option>
                  <option value="RR">RR</option>
                  <option value="SC">SC</option>
                  <option value="SP">SP</option>
                  <option value="SE">SE</option>
                  <option value="TO">TO</option>
                </select>
              </div>
            </div>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800">
                Ao salvar, o endereço será automaticamente geocodificado para obter as coordenadas de latitude e longitude.
              </p>
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={voltarParaDashboard}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-blue-600 hover:bg-blue-700"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Salvando...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Salvar Endereço
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default EnderecoEmpresa;
